/**
 * usePlanContext — Hook for accessing the current property's subscription plan.
 *
 * Fetches plan data from the property settings endpoint and provides:
 *   - plan: "host" | "insight" | "concierge"
 *   - status: "trialing" | "active" | "past_due" | "trial_expired" | "no_subscription"
 *   - isTrialing: boolean
 *   - trialEndsAt: string | null
 *   - degraded: boolean (true if post-trial with no subscription)
 *   - blurredModules: array of modules to blur for this tier
 *   - canAccess(feature): convenience function
 *
 * Usage:
 *   const { plan, canAccess, isTrialing } = usePlanContext()
 *   if (!canAccess("performance.ai_narrative")) { showBlurred() }
 */

import { useCallback, useEffect, useMemo, useState } from "react"
import { apiClient } from "@/lib/api"
import { usePropertyContext } from "@/lib/usePropertyContext"

// ---------------------------------------------------------------------------
// Feature matrix (client-side mirror of plan-features.js)
// ---------------------------------------------------------------------------
const PLAN_TIER_RANK: Record<string, number> = { host: 0, insight: 1, concierge: 2 }

function normalizePlan(plan: string): string {
  const p = (plan || "").toLowerCase().trim()
  if (p === "free") return "host"
  if (p === "pro") return "insight"
  if (p === "enterprise") return "concierge"
  if (p === "trialing" || p === "trial") return "concierge"
  if (PLAN_TIER_RANK[p] !== undefined) return p
  return "host"
}

function nextTierForPlan(plan: string): string | null {
  const normalized = normalizePlan(plan)
  if (normalized === "host") return "insight"
  if (normalized === "insight") return "concierge"
  return null
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface BlurredModule {
  page: string
  feature: string
  label: string
  upgrade_to: string
  cta: string
}

interface PlanContext {
  plan: string
  status: string
  isTrialing: boolean
  trialEndsAt: string | null
  degraded: boolean
  blurredModules: BlurredModule[]
  nextTier: string | null
  isLoading: boolean
  error: string | null
  canAccess: (feature: string) => boolean
  reload: () => Promise<void>
}

interface ServerPlanPayload {
  plan: string
  status: string
  is_trialing: boolean
  trial_ends_at: string | null
  degraded: boolean
  blurred_modules: BlurredModule[]
  next_tier: string | null
}

function buildLegacyPlanPayload(rawTier: string): ServerPlanPayload {
  const normalized = normalizePlan(rawTier)
  return {
    plan: normalized,
    status: "active",
    is_trialing: false,
    trial_ends_at: null,
    degraded: false,
    blurred_modules: [],
    next_tier: nextTierForPlan(normalized),
  }
}

// ---------------------------------------------------------------------------
// Client-side feature matrix (subset for common checks)
// Full matrix is on the server; this is for UI-level gating
// ---------------------------------------------------------------------------
const CLIENT_FEATURE_MATRIX: Record<string, string> = {
  "performance.ai_narrative": "insight",
  "performance.ai_ranked_drivers": "insight",
  "performance.ai_weekly_actions": "insight",
  "performance.ai_findings": "insight",

  "reviews.ai_analysis": "insight",
  "reviews.ai_cross_platform": "insight",
  "reviews.ai_multilang_risks": "insight",
  "guest.house_voice": "insight",
  "guest.house_voice_full": "concierge",
  "guest.guidebook_welcome_ai": "concierge",
  "guest.guidebook_headline_ai": "concierge",
  "guest.guidebook_body_ai": "concierge",
  "guest.guidebook_places_ai": "concierge",
  "display.eink_headline_ai": "concierge",
  "display.eink_prose_ai": "concierge",
  "display.tv_welcome_ai": "concierge",
  "display.tv_places_ai": "concierge",
  "research.batch_research": "concierge",

  "platform.portfolio_view": "concierge",
  "platform.intelligence_gate": "insight",
}

function canAccessFeature(plan: string, feature: string): boolean {
  const normalPlan = normalizePlan(plan)
  const requiredTier = CLIENT_FEATURE_MATRIX[feature]
  if (!requiredTier) return true // unknown feature = allow
  return (PLAN_TIER_RANK[normalPlan] ?? 0) >= (PLAN_TIER_RANK[requiredTier] ?? 0)
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export function usePlanContext(): PlanContext {
  const { propertyId, property } = usePropertyContext()
  const [planData, setPlanData] = useState<ServerPlanPayload | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadPlan = useCallback(async () => {
    if (!propertyId) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      // Primary path: property settings endpoint (includes plan context)
      let resolved: ServerPlanPayload | null = null

      const res = await apiClient.getPropertySettings(propertyId)
      const settings = res.settings as Record<string, unknown> | undefined
      const planObj = settings?.plan as ServerPlanPayload | undefined
      const settingsProperty = settings?.property as Record<string, unknown> | undefined
      const legacyTierRaw = typeof settingsProperty?.tier === "string" ? settingsProperty.tier : ""

      if (res.ok && planObj && typeof planObj === "object" && "plan" in planObj) {
        resolved = planObj
      } else if (res.ok && legacyTierRaw.trim()) {
        resolved = buildLegacyPlanPayload(legacyTierRaw)
      }

      // Fallback: if property settings returned no plan or degraded host,
      // query billing status directly (user-scoped, not property-scoped).
      // This catches cases where the property→owner→subscription chain fails
      // silently but the user actually has an active subscription/trial.
      if (!resolved || (resolved.degraded && resolved.plan === "host")) {
        try {
          const billing = await apiClient.getBillingStatus()
          const sub = billing.ok ? billing.billing?.subscription : null
          if (sub && sub.status !== "canceled" && sub.status !== "expired") {
            const effectivePlan = sub.is_trialing ? "concierge" : normalizePlan(sub.plan)
            const effectiveStatus = sub.is_trialing ? "trialing" : sub.status
            resolved = {
              plan: effectivePlan,
              status: effectiveStatus,
              is_trialing: sub.is_trialing,
              trial_ends_at: sub.trial_ends_at,
              degraded: false,
              blurred_modules: [],
              next_tier: nextTierForPlan(effectivePlan),
            }
          }
        } catch {
          // Billing fallback failed — keep whatever we had from settings
        }
      }

      setPlanData(resolved || {
        plan: "host",
        status: "no_subscription",
        is_trialing: false,
        trial_ends_at: null,
        degraded: true,
        blurred_modules: [],
        next_tier: "insight",
      })
    } catch (err) {
      setError(String(err))

      // Even on settings error, try billing status as standalone fallback
      try {
        const billing = await apiClient.getBillingStatus()
        const sub = billing.ok ? billing.billing?.subscription : null
        if (sub && sub.status !== "canceled" && sub.status !== "expired") {
          const effectivePlan = sub.is_trialing ? "concierge" : normalizePlan(sub.plan)
          const effectiveStatus = sub.is_trialing ? "trialing" : sub.status
          setPlanData({
            plan: effectivePlan,
            status: effectiveStatus,
            is_trialing: sub.is_trialing,
            trial_ends_at: sub.trial_ends_at,
            degraded: false,
            blurred_modules: [],
            next_tier: nextTierForPlan(effectivePlan),
          })
          setError(null)
          return
        }
      } catch {
        // Both paths failed
      }

      const ctxTier = typeof (property as { tier?: unknown } | null)?.tier === "string"
        ? String((property as { tier?: string }).tier || "")
        : ""
      if (ctxTier.trim()) {
        setPlanData(buildLegacyPlanPayload(ctxTier))
      } else {
        setPlanData({
          plan: "host",
          status: "unknown",
          is_trialing: false,
          trial_ends_at: null,
          degraded: false,
          blurred_modules: [],
          next_tier: "insight",
        })
      }
    } finally {
      setIsLoading(false)
    }
  }, [propertyId, property])

  useEffect(() => {
    loadPlan()
  }, [loadPlan])

  const plan = normalizePlan(planData?.plan || "host")
  const blockedByServer = useMemo(() => {
    const items = Array.isArray(planData?.blurred_modules) ? planData.blurred_modules : []
    return new Set(items.map((item) => item.feature))
  }, [planData?.blurred_modules])

  const canAccess = useCallback(
    (feature: string) => {
      if (blockedByServer.has(feature)) return false
      return canAccessFeature(plan, feature)
    },
    [plan, blockedByServer],
  )

  return useMemo<PlanContext>(
    () => ({
      plan,
      status: planData?.status || "unknown",
      isTrialing: planData?.is_trialing || false,
      trialEndsAt: planData?.trial_ends_at || null,
      degraded: planData?.degraded || false,
      blurredModules: planData?.blurred_modules || [],
      nextTier: planData?.next_tier || null,
      isLoading,
      error,
      canAccess,
      reload: loadPlan,
    }),
    [plan, planData, isLoading, error, canAccess, loadPlan],
  )
}

// ---------------------------------------------------------------------------
// Utility: format trial remaining
// ---------------------------------------------------------------------------
export function formatTrialRemaining(trialEndsAt: string | null): string | null {
  if (!trialEndsAt) return null
  const end = new Date(trialEndsAt)
  const now = new Date()
  const diffMs = end.getTime() - now.getTime()
  if (diffMs <= 0) return "expired"
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
  if (days === 1) return "1 day left"
  return `${days} days left`
}

// ---------------------------------------------------------------------------
// Export types
// ---------------------------------------------------------------------------
export type { PlanContext, BlurredModule }
